import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE, GREEN] = CHART_COLORS;

function Arrow({
  id,
  x1,
  y1,
  x2,
  y2,
  color = CHART_INK.text,
  width = 2.5,
}: {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color?: string;
  width?: number;
}) {
  return (
    <line
      markerEnd={`url(#${id})`}
      stroke={color}
      strokeWidth={width}
      x1={x1}
      x2={x2}
      y1={y1}
      y2={y2}
    />
  );
}

function Label({
  x,
  y,
  children,
  anchor = "middle",
  color = CHART_INK.text,
}: {
  x: number;
  y: number;
  children: string;
  anchor?: "start" | "middle" | "end";
  color?: string;
}) {
  return (
    <text
      fill={color}
      fontSize="15"
      fontStyle="italic"
      textAnchor={anchor}
      x={x}
      y={y}
    >
      {children}
    </text>
  );
}

function Head({ id, color = CHART_INK.text }: { id: string; color?: string }) {
  return (
    <marker
      id={id}
      markerHeight="8"
      markerWidth="8"
      orient="auto"
      refX="7"
      refY="4"
    >
      <path d="M0,0 L8,4 L0,8 z" fill={color} />
    </marker>
  );
}

/** A force F pressing on a surface of area A, spread as pressure P. */
export function ForceAreaDiagram({
  id,
  label,
  description,
}: {
  id: string;
  label: string;
  description: string;
}) {
  const arrow = `${id}-arrow`;
  return (
    <ChartSvg
      description={description}
      height={190}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <Head id={arrow} />
        <Head color={ORANGE} id={`${id}-orange`} />
      </defs>
      <Arrow id={arrow} x1={280} x2={280} y1={14} y2={78} />
      <Label anchor="start" x={292} y={44}>
        F
      </Label>
      <rect
        fill={BLUE}
        fillOpacity="0.85"
        height="34"
        width="150"
        x={205}
        y={82}
      />
      <rect fill={CHART_INK.grid} height="26" width="420" x={70} y={116} />
      {[225, 255, 285, 315, 345].map((x) => (
        <Arrow
          color={ORANGE}
          id={`${id}-orange`}
          key={x}
          width={2}
          x1={x}
          x2={x}
          y1={120}
          y2={150}
        />
      ))}
      <line
        markerEnd="none"
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={205}
        x2={355}
        y1={170}
        y2={170}
      />
      <line
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={205}
        x2={205}
        y1={164}
        y2={176}
      />
      <line
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={355}
        x2={355}
        y1={164}
        y2={176}
      />
      <Label x={280} y={186}>
        A
      </Label>
      <Label anchor="start" color={ORANGE} x={372} y={142}>
        P = F / A
      </Label>
    </ChartSvg>
  );
}

/** A tank of fluid at depth h, with the atmosphere P₀ on top and ρgh building below. */
export function FluidDiagram({
  id,
  label,
  description,
}: {
  id: string;
  label: string;
  description: string;
}) {
  const arrow = `${id}-arrow`;
  const orange = `${id}-orange`;
  const top = 64;
  const bottom = 196;
  return (
    <ChartSvg
      description={description}
      height={236}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <Head id={arrow} />
        <Head color={ORANGE} id={orange} />
        <Head color={GREEN} id={`${id}-green`} />
      </defs>
      <rect
        fill={BLUE}
        fillOpacity="0.25"
        height={bottom - top}
        width="300"
        x={130}
        y={top}
      />
      <path
        d={`M 130 ${top - 4} L 130 ${bottom} L 430 ${bottom} L 430 ${top - 4}`}
        fill="none"
        stroke={CHART_INK.text}
        strokeWidth="3"
      />
      {[170, 230, 290, 350, 400].map((x) => (
        <Arrow
          color={GREEN}
          id={`${id}-green`}
          key={x}
          width={2}
          x1={x}
          x2={x}
          y1={14}
          y2={top - 4}
        />
      ))}
      <Label anchor="start" color={GREEN} x={446} y={36}>
        P₀
      </Label>
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="4 3"
        x1={100}
        x2={130}
        y1={top}
        y2={top}
      />
      <line
        markerEnd={`url(#${arrow})`}
        markerStart={`url(#${arrow})`}
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={104}
        x2={104}
        y1={top}
        y2={bottom}
      />
      <Label x={88} y={(top + bottom) / 2 + 5}>
        h
      </Label>
      {[150, 210, 270, 330, 400].map((x) => (
        <Arrow
          color={ORANGE}
          id={orange}
          key={x}
          width={2}
          x1={x}
          x2={x}
          y1={bottom - 52}
          y2={bottom - 6}
        />
      ))}
      <Label anchor="start" color={ORANGE} x={446} y={bottom - 20}>
        ρ g h
      </Label>
      <Label x={280} y={(top + bottom) / 2 - 6}>
        ρ
      </Label>
    </ChartSvg>
  );
}
